import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hubAPI } from '../../services/hubApi';
import { programAPI } from '../../services/programApi';
import { fetchCatalogResources } from '../../services/resourceCatalogApi';
import { wordpressAPI } from '../../services/wordpressApi';
import { Hub } from '../../types/hub';
import { Program } from '../../types/program';
import { CatalogResource } from '../../types/resourceCatalog';
import { decodeHtmlEntities } from '../../utils/htmlEntities';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash';
import Spinner from '../ui/Spinner';
import QuietAction from '../cards/QuietAction';
import HubRow from '../hubs/HubRow';
import ResourceRow from '../resources/ResourceRow';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { hubWord } from '../../utils/cardFacts';
import { filterHubs, hubHasDocuments, programMatchesQuery } from '../../utils/hubSearch';
import { hubPlaceHref } from '../../utils/hubChapter';
import { placePath } from '../../utils/placePaths';
import { placedDocumentIds, rankCatalogResources } from '../../utils/searchRank';
import { ORBCON_REPORT_CATEGORY_ID } from '../../content/orbconReport';
import { isUnderRevision } from '../../utils/resourceRevision';
import { beginPlaceOpen, PlaceSurface } from '../../utils/placeMotion';

interface SearchAutocompleteProps {
  onClose?: () => void;
  className?: string;
  size?: 'default' | 'large';
  autoFocus?: boolean;
  initialQuery?: string;
  /** Limit results to one practice area. Whole-site search stays one click away. */
  scopeProgram?: { slug: string; title: string };
}

type SearchHit =
  | { kind: 'program'; program: Program }
  | { kind: 'hub'; hub: Hub }
  | { kind: 'resource'; resource: CatalogResource };

const LIST_CACHE_MS = 60_000;
let hubCache: { data: Hub[]; at: number } | null = null;
let programCache: { data: Program[]; at: number } | null = null;

async function loadPublishedHubs(): Promise<Hub[]> {
  if (hubCache && Date.now() - hubCache.at < LIST_CACHE_MS) {
    return hubCache.data;
  }
  const data = await hubAPI.fetchHubs();
  hubCache = { data, at: Date.now() };
  return data;
}

async function loadPublishedPrograms(): Promise<Program[]> {
  if (programCache && Date.now() - programCache.at < LIST_CACHE_MS) {
    return programCache.data;
  }
  const data = await programAPI.fetchPrograms();
  programCache = { data, at: Date.now() };
  return data;
}

export default function SearchAutocomplete({
  onClose,
  className = '',
  size = 'default',
  autoFocus = false,
  initialQuery = '',
  scopeProgram,
}: SearchAutocompleteProps) {
  const [query, setQuery] = useState(initialQuery);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const scopeRef = useRef(scopeProgram);
  scopeRef.current = scopeProgram;
  const resultsRef = useRef<HTMLDivElement>(null);

  const goToSearchPage = (searchQuery: string) => {
    const params = new URLSearchParams({ q: searchQuery.trim() });
    if (scopeProgram) params.set('program', scopeProgram.slug);
    navigate(`/search?${params.toString()}`);
    setQuery('');
    setShowResults(false);
    onClose?.();
  };

  const debouncedSearch = useRef(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setHits([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const catalogParams = new URLSearchParams({
          q: searchQuery,
          perPage: '20',
          sort: 'relevance',
        });
        const [resources, hubs, programs] = await Promise.all([
          fetchCatalogResources(catalogParams)
            .then((body) => body.data)
            .catch(() => [] as CatalogResource[]),
          loadPublishedHubs().catch(() => [] as Hub[]),
          loadPublishedPrograms().catch(() => [] as Program[]),
        ]);
        const wpPosts = resources.length
          ? await wordpressAPI
              .fetchPosts({
                include: resources.map((resource) => resource.wpId).join(','),
                per_page: resources.length,
              })
              .catch(() => [])
          : [];
        const wpById = new Map(wpPosts.map((post) => [post.id, post]));
        const ranked = rankCatalogResources(
          resources,
          wpById,
          placedDocumentIds(hubs, programs),
          ORBCON_REPORT_CATEGORY_ID
        );
        const scopeNow = scopeRef.current;
        const scopedProgram = scopeNow ? programs.find((item) => item.slug === scopeNow.slug) : undefined;
        const scopedIds = scopedProgram
          ? placedDocumentIds(
              hubs.filter((hub) => hub.programId === scopedProgram.id || hub.program?.slug === scopedProgram.slug),
              [scopedProgram]
            )
          : null;
        const scopedDocuments = scopedIds
          ? ranked.documents.filter((resource) => scopedIds.has(resource.wpId))
          : ranked.documents;
        const scope = scopeRef.current;
        const rankedResources = [...(scope ? scopedDocuments : ranked.documents), ...(scope ? [] : ranked.news)].slice(0, 5);
        const matchedPrograms = programs
          .filter((program) => programMatchesQuery(program, searchQuery))
          .filter((program) => !scope || program.slug === scope.slug)
          .slice(0, 3);
        const programHits: SearchHit[] = matchedPrograms.map((program) => ({ kind: 'program', program }));
        const matchedProgramIds = new Set(
          (scope ? programs.filter((program) => program.slug === scope.slug) : matchedPrograms).map((program) => program.id)
        );
        const hubHits: SearchHit[] = filterHubs(hubs, searchQuery)
          .filter((hub) => hubHasDocuments(hub) && !(hub.programId && matchedProgramIds.has(hub.programId)))
          .filter((hub) => !scope || hub.program?.slug === scope.slug || matchedProgramIds.has(hub.programId ?? ''))
          .slice(0, 3)
          .map((hub) => ({ kind: 'hub', hub }));
        const resourceHits: SearchHit[] = rankedResources.map((resource) => {
          const wp = wpById.get(resource.wpId);
          return {
            kind: 'resource' as const,
            resource: {
              ...resource,
              underRevision: wp ? isUnderRevision(wp.acf?.under_revision) : resource.underRevision === true,
            },
          };
        });
        setHits([...programHits, ...hubHits, ...resourceHits]);
      } catch (error) {
        console.error('Search error:', error);
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    if (!initialQuery) return;
    setQuery(initialQuery);
    setShowResults(true);
  }, [initialQuery]);

  useEffect(() => {
    if (query) {
      debouncedSearch(query);
      setShowResults(true);
    } else {
      setHits([]);
      setShowResults(false);
    }
  }, [query, debouncedSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      goToSearchPage(query);
    }
  };

  const destinationFor = (hit: SearchHit): { to: string; surface: PlaceSurface } => {
    if (hit.kind === 'program') {
      return {
        to: placePath(hit.program),
        surface: { kind: 'program', slug: hit.program.slug },
      };
    }
    if (hit.kind === 'hub') {
      return { to: hubPlaceHref(hit.hub), surface: { kind: 'hub', slug: hit.hub.slug } };
    }
    return {
      to: `/resources/${hit.resource.slug}`,
      surface: { kind: 'resource', slug: hit.resource.slug },
    };
  };

  const handleSelect = (hit: SearchHit, origin: HTMLElement | null) => {
    const destination = destinationFor(hit);
    const selectedQuery = query.trim();
    beginPlaceOpen(
      origin,
      { ...destination.surface, via: 'search' },
      destination.to,
      navigate,
      location,
      () => {
        setQuery('');
        setShowResults(false);
        onClose?.();
      },
      selectedQuery ? { reopenSearch: selectedQuery } : undefined
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < hits.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const origin = resultsRef.current?.children[selectedIndex] as HTMLElement | undefined;
      handleSelect(hits[selectedIndex], origin ?? null);
    } else if (e.key === 'Escape') {
      setShowResults(false);
      onClose?.();
    }
  };

  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex] as HTMLElement;
      selectedElement?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <MagnifyingGlassIcon className={`absolute top-1/2 transform -translate-y-1/2 text-gray-400 ${size === 'large' ? 'left-5 h-5 w-5' : 'left-3 h-5 w-5'}`} />
          <input
            ref={inputRef}
            type="text"
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(-1);
            }}
            onFocus={() => query && setShowResults(true)}
            onKeyDown={handleKeyDown}
            placeholder={
              scopeProgram
                ? `Search ${scopeProgram.title}`
                : 'Search programs, toolkits, and references...'
            }
            className={`w-full focus:ring-2 focus:ring-primary focus:border-primary ${
              size === 'large'
                ? 'pl-12 pr-10 py-4 text-base border-0 rounded-full shadow-xl focus:shadow-2xl transition-shadow'
                : 'pl-10 pr-10 py-2 border border-gray-300 rounded-md bg-white'
            }`}
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setHits([]);
                setShowResults(false);
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </form>

      {showResults && (query.length >= 2 || hits.length > 0) && (
        <div
          className={`absolute z-50 w-full bg-white border border-gray-200 shadow-lg overflow-hidden ${
            size === 'large' ? 'mt-3 rounded-2xl' : 'mt-2 rounded-lg'
          }`}
        >
          <div ref={resultsRef} className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="p-4 text-center">
                <Spinner size={50} className="mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Searching...</p>
              </div>
            )}

            {!loading && hits.length === 0 && query.length >= 2 && (
              <div className="p-4 text-center text-gray-500">
                No results found for "{query}"
              </div>
            )}

            {!loading && hits.length > 0 && (
              <>
                {hits.map((hit, index) => {
                  if (hit.kind === 'program') {
                    const destination = destinationFor(hit);
                    return (
                      <div
                        key={`program-${hit.program.id}`}
                        data-search-hit
                        className={`flex items-start gap-4 w-full p-4 bg-white border-b border-gray-100 last:border-b-0 ${
                          index === selectedIndex ? 'bg-gray-50' : ''
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-primary">{hit.program.kind === 'REFERENCE' ? 'Reference' : 'Program'}</p>
                          <p className="text-base font-semibold text-gray-900">{hit.program.title}</p>
                          <p className="text-sm text-gray-600">{hubWord(hit.program.hubs.length)} inside</p>
                        </div>
                        <div className="flex flex-col items-end">
                          <SaveToMyOrbconButton
                            type="PROGRAM"
                            programId={hit.program.id}
                            title={hit.program.title}
                            slug={hit.program.slug}
                            variant="icon"
                            align="end"
                          />
                          <QuietAction
                            to={destination.to}
                            label={`Enter ${hit.program.title}`}
                            onClick={(event) => {
                              event.preventDefault();
                              const row = event.currentTarget.closest('[data-search-hit]');
                              handleSelect(hit, row instanceof HTMLElement ? row : null);
                            }}
                          >
                            Enter
                          </QuietAction>
                        </div>
                      </div>
                    );
                  }

                  if (hit.kind === 'hub') {
                    return (
                      <div
                        key={`hub-${hit.hub.id}`}
                        data-search-hit
                        className={`border-b border-gray-100 last:border-b-0 ${
                          index === selectedIndex ? 'bg-gray-50' : ''
                        }`}
                      >
                        <HubRow search hub={hit.hub} />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`resource-${hit.resource.wpId}`}
                      data-search-hit
                      className={index === selectedIndex ? 'bg-gray-50' : undefined}
                    >
                      <ResourceRow
                        id={hit.resource.wpId}
                        title={decodeHtmlEntities(hit.resource.title)}
                        slug={hit.resource.slug}
                        date={hit.resource.wpCreatedAt}
                        underRevision={hit.resource.underRevision === true}
                        onActivate={(origin) => handleSelect(hit, origin)}
                      />
                    </div>
                  );
                })}
                <div className="p-3 border-t border-gray-200 bg-gray-50">
                  <button
                    onClick={handleSubmit}
                    className="w-full text-sm text-primary hover:text-primary-dark font-medium"
                  >
                    {scopeProgram ? `View results in ${scopeProgram.title}` : `View all results for "${query}"`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
